import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import adminService from '../admin/services/adminService';

// Initial state
const initialState = {
  isAuthenticated: false,
  admin: null,
  dashboard: {
    stats: null,
    loading: false,
    error: null
  },
  users: {
    data: [],
    loading: false,
    error: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 10,
      pages: 0
    }
  },
  companies: {
    data: [],
    loading: false,
    error: null,
    pagination: {
      total: 0,
      page: 1,
      limit: 10,
      pages: 0
    }
  },
  affiliate: {
    stats: null,
    loading: false,
    error: null
  },
  instagramAnalytics: {
    data: null,
    loading: false,
    error: null
  },
  activeTab: 'dashboard',
  loading: false,
  error: null
};

// Async thunks
export const loginAdmin = createAsyncThunk(
  'admin/login',
  async (credentials, { rejectWithValue }) => {
    try {
      const response = await adminService.adminLogin(credentials);
      // ✅ Store userType in localStorage
      localStorage.setItem('userType', 'admin');
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Login failed');
    }
  }
);

export const logoutAdmin = createAsyncThunk(
  'admin/logout',
  async (_, { rejectWithValue }) => {
    try {   
      await adminService.adminLogout();
      // ✅ Remove userType from localStorage
      localStorage.removeItem('userType');
      return null;
    } catch (error) {
      return rejectWithValue(error.message || 'Logout failed');
    }
  }
);

export const fetchDashboardStats = createAsyncThunk(
  'admin/fetchDashboardStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getDashboardStats();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch dashboard stats');
    }
  }
);

export const fetchUsers = createAsyncThunk(
  'admin/fetchUsers',
  async ({ page = 1, limit = 10, search = '', filters = {} }, { rejectWithValue }) => {
    try {
      const response = await adminService.getUsers(page, limit, search, filters);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch users');
    }
  }
);

export const fetchCompanies = createAsyncThunk(
  'admin/fetchCompanies',
  async ({ page = 1, limit = 10, search = '' }, { rejectWithValue }) => {
    try {
      const response = await adminService.getCompanies(page, limit, search);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch companies');
    }
  }
);

export const updateUser = createAsyncThunk(
  'admin/updateUser',
  async ({ userId, payload }, { rejectWithValue }) => {
    try {
      const response = await adminService.updateUser(userId, payload);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update user');
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  'admin/toggleUserStatus',
  async ({ userId, status }, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleUserStatus(userId, status);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update user status');
    }
  }
);

export const toggleCompanyVerification = createAsyncThunk(
  'admin/toggleCompanyVerification',
  async ({ companyId, isVerified }, { rejectWithValue }) => {
    try {
      const response = await adminService.toggleCompanyVerification(companyId, isVerified);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update company verification');
    }
  }
);

export const fetchAffiliateStats = createAsyncThunk(
  'admin/fetchAffiliateStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getAffiliateStats();
      // adminService already returns parsed JSON from the API,
      // so we forward it directly to reducers.
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch affiliate statistics');
    }
  }
);

export const fetchInstagramLoginAnalytics = createAsyncThunk(
  'admin/fetchInstagramLoginAnalytics',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminService.getInstagramLoginAnalytics();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch Instagram login analytics');
    }
  }
);

// Admin slice
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setAdmin: (state, action) => {
      state.admin = action.payload;
      state.isAuthenticated = true;
    },
    clearErrors(state) {
      state.error = null;
      state.users.error = null;
      state.companies.error = null;
      state.dashboard.error = null;
      state.affiliate.error = null;
      state.instagramAnalytics.error = null;
    },
    setActiveTab(state, action) {
      state.activeTab = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginAdmin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginAdmin.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.admin = action.payload.admin;
        // ✅ Store userType in localStorage
        localStorage.setItem('userType', 'admin');
      })
      .addCase(loginAdmin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Logout cases
      .addCase(logoutAdmin.fulfilled, (state) => {
        // ✅ Remove userType from localStorage
        localStorage.removeItem('userType');
        return initialState;
      })
      
      // Dashboard stats cases
      .addCase(fetchDashboardStats.pending, (state) => {
        state.dashboard.loading = true;
        state.dashboard.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.dashboard.loading = false;
        state.dashboard.stats = action.payload;
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.dashboard.loading = false;
        state.dashboard.error = action.payload;
      })
      
      // Users fetching cases
      .addCase(fetchUsers.pending, (state) => {
        state.users.loading = true;
        state.users.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.users.loading = false;
        state.users.data = action.payload.users;
        state.users.pagination = action.payload.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.users.loading = false;
        state.users.error = action.payload;
      })
      
      // Companies fetching cases
      .addCase(fetchCompanies.pending, (state) => {
        state.companies.loading = true;
        state.companies.error = null;
      })
      .addCase(fetchCompanies.fulfilled, (state, action) => {
        state.companies.loading = false;
        state.companies.data = action.payload.companies;
        state.companies.pagination = action.payload.pagination;
      })
      .addCase(fetchCompanies.rejected, (state, action) => {
        state.companies.loading = false;
        state.companies.error = action.payload;
      })
      
      // Update user cases (e.g. contractStatus)
      .addCase(updateUser.fulfilled, (state, action) => {
        const updated = action.payload.data;
        if (updated && updated._id) {
          const userIndex = state.users.data.findIndex(user => user._id === updated._id);
          if (userIndex !== -1) {
            state.users.data[userIndex] = { ...state.users.data[userIndex], ...updated };
          }
        }
      })
      // Toggle user status cases
      .addCase(toggleUserStatus.fulfilled, (state, action) => {
        const { userId, status } = action.payload;
        // Update user in the list
        const userIndex = state.users.data.findIndex(user => user._id === userId);
        if (userIndex !== -1) {
          state.users.data[userIndex].status = status;
        }
      })
      
      // Toggle company verification cases
      .addCase(toggleCompanyVerification.fulfilled, (state, action) => {
        const { companyId, isVerified } = action.payload;
        // Update company in the list
        const companyIndex = state.companies.data.findIndex(company => company._id === companyId);
        if (companyIndex !== -1) {
          state.companies.data[companyIndex].isVerified = isVerified;
        }
      })
      
      // Affiliate stats cases
      .addCase(fetchAffiliateStats.pending, (state) => {
        state.affiliate.loading = true;
        state.affiliate.error = null;
      })
      .addCase(fetchAffiliateStats.fulfilled, (state, action) => {
        state.affiliate.loading = false;
        state.affiliate.stats = action.payload;
      })
      .addCase(fetchAffiliateStats.rejected, (state, action) => {
        state.affiliate.loading = false;
        state.affiliate.error = action.payload;
      })
      
      // Instagram analytics cases
      .addCase(fetchInstagramLoginAnalytics.pending, (state) => {
        state.instagramAnalytics.loading = true;
        state.instagramAnalytics.error = null;
      })
      .addCase(fetchInstagramLoginAnalytics.fulfilled, (state, action) => {
        state.instagramAnalytics.loading = false;
        state.instagramAnalytics.data = action.payload;
      })
      .addCase(fetchInstagramLoginAnalytics.rejected, (state, action) => {
        state.instagramAnalytics.loading = false;
        state.instagramAnalytics.error = action.payload;
      });
  }
});

export const { setAdmin, clearErrors, setActiveTab } = adminSlice.actions;
export default adminSlice.reducer; 